import type { Cursor } from "@nabla/api";
import { latest, oldest, storeOf, type DurableEvent } from "./cursor.ts";
import { createStream, type StreamDeps } from "./stream.ts";

interface FeedDeps extends Omit<StreamDeps, "applied" | "onSkipped"> {
  readonly generation: () => number;
  readonly reloadAll: () => Promise<Cursor[]>;
}

export function createFeed(deps: FeedDeps) {
  let applied: Cursor | null = null;
  let resetting = false;
  const stream = createStream({
    ...deps,
    applied: () => applied ?? { r: 0, c: 0, p: 0, a: 0 },
    onSkipped(to) {
      if (applied !== null) applied = latest(applied, to);
    },
  });
  return {
    applied: () => applied,
    firstSnapshot(cursor: Cursor): void {
      if (applied !== null || resetting) return;
      applied = cursor;
      stream.start();
    },
    advance(event: DurableEvent): boolean {
      if (applied === null) return false;
      const store = storeOf(event);
      if (event.cursor[store] <= applied[store]) return false;
      applied = { ...applied, [store]: event.cursor[store] };
      return true;
    },
    async catchUp(): Promise<void> {
      if (resetting) return;
      const session = deps.generation();
      resetting = true;
      applied = null;
      stream.stop();
      const [first, ...rest] = await deps.reloadAll();
      if (deps.generation() !== session) return;
      applied = first === undefined ? null : oldest(first, ...rest);
      resetting = false;
      if (applied !== null) stream.start();
    },
    stop: stream.stop,
    forget(): void {
      stream.stop();
      applied = null;
      resetting = false;
    },
  };
}
